/**
 * @file The SettingsSidebarProvider wraps all children and provides the SettingsSidebar context
 */

import type { Context } from "@moe/priv/components/ui/sidebar";
import { SidebarProvider as UISidebarProvider, useSidebar } from "@moe/priv/components/ui/sidebar";
import { SettingsSidebar } from "@web/route-services/chat/settings/SettingsSidebar";
import { createContext, useContext } from "react";

/**
 * Extends the original ShadCN SidebarContext with extra attributes that we need
 */
interface Props extends Context {}

const SettingsSidebarContext = createContext<Props>({} as Props);

export function SettingsSidebarProvider({ chatID, children }: { chatID?: number; children: React.ReactNode }) {
  return (
    <UISidebarProvider defaultOpen={false}>
      <Inner chatID={chatID}>{children}</Inner>
    </UISidebarProvider>
  );
}

export const useSettingsSidebarContext = () => {
  const context = useContext(SettingsSidebarContext);
  if (!context) {
    throw new Error("useSettingsSidebarContext must be used within a SettingsSidebarProvider");
  }
  return context;
};

function Inner({ chatID, children }: { chatID?: number; children: React.ReactNode }) {
  const props = useSidebar();

  return (
    <SettingsSidebarContext.Provider value={{ ...props }}>
      {chatID && <SettingsSidebar chatID={chatID} />}
      {children}
    </SettingsSidebarContext.Provider>
  );
}
